import { useState, useEffect, useContext } from "react";
import NumberFormat from "react-number-format";
import { Link, useNavigate } from "react-router-dom";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import ReadMore from "components/UpStyled/ReadMore";
import { Badge } from "antd";

import { Tabs, Tag } from "antd";

import StyleImages from "./StyleImages";
import { getStyleImages } from "utils/imageHelper";
import "./style.css";

import toast from "react-hot-toast";

import JSZip from "jszip";
import FileSaver from "file-saver";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { AccountContext } from "context/account";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import api from "services/api";
import CreateColorModal from "pages/de/palette/components/CreateColorModal";

const ProjectStyles = ({ project }) => {
  const [colorOptions, setColorOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [colorDetailVisible, setColorDetailVisible] = useState(false);
  const [selectedColor, setSelectedColor] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    if (project.id) {
      retrievePalette();
      retrieveSizeCharts();
    }
  }, [project.id]);

  const account = useContext(AccountContext);

  const handleDownloadStyle = (data = [], folder = "files") => {
    const zip = new JSZip();
    const promises = []; //用于存储多个promise

    //project sketches files
    for (let i = 0; i < data.length; i++) {
      const fileFolder = zip.folder(folder);
      for (let i = 0; i < data.length; i++) {
        if (data[i]?.url) {
          const promise = getFile(data[i].url).then((res) => {
            const fileName = data[i]?.name || "file_" + i;
            fileFolder.file(fileName, res, { binary: true });
          });
          promises.push(promise);
        }
      }
    }

    if (promises && promises.length > 0) {
      const toastId = toast.loading("Start downing project files...");
      Promise.all(promises)
        .then(() => {
          zip
            .generateAsync({
              type: "blob",
              compression: "DEFLATE", // STORE：默认不压缩 DEFLATE：需要压缩
              compressionOptions: {
                level: 4, // 压缩等级1~9    1压缩速度最快，9最优压缩方式
              },
            })
            .then((res) => {
              toast.success("Download project files started successfully!");
              FileSaver.saveAs(res, folder);
            })
            .catch((err) => {
              toast.dismiss(toastId);
            });

          toast.dismiss(toastId);
        })
        .catch((err) => console.log("download failed!"));
    } else {
      toast.error("No available project files!");
    }
  };

  const getFile = (url) => {
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: "GET",
      })
        .then((response) => response.blob())
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const retrievePalette = async () => {
    let payload = {
      order: "-updated_at",
      owner_id: project.owner_id,
    };

    try {
      const { data } = await api.palette.list(payload);

      const colors = data["colors"] ? data["colors"] : [];
      const options = colors.map((x) => ({ label: x.name, value: x.id, hex: x.hex, meta: x }));
      setColorOptions(options);
    } catch (err) {
      console.log(err);
    }
  };

  const retrieveSizeCharts = async () => {
    try {
      const { data } = await api.sizeChart.list({
        order: "-updated_at",
        owner_id: project?.owner_id,
      });

      let ownSizeCharts = data["sizes"] ? data["sizes"] : [];

      const shared = await api.sizeChart.sharedList({
        order: "-updated_at",
      });

      let sharedSizeCharts = shared?.data?.sizes || [];
      sharedSizeCharts = sharedSizeCharts.map((x) => ({ ...x, name: "Shared:" + x.name }));

      const allSizeCharts = [...ownSizeCharts, ...sharedSizeCharts];

      const options = allSizeCharts.map((x) => ({
        label: x.name,
        value: x.id,
        meta: {
          id: x.id,
          name: x.name,
          gender: x.gender,
          category: x.category,
          sizes: x.sizes,
          class: x.class,
          attribute: x.attribute,
        },
      }));
      setSizeOptions(options);
    } catch (err) {
      console.log(err);
    }
  };

  const handleShowColorDetail = (colorOption) => {
    if (account.id == project.owner_id) {
      navigate(`/setup/color-master/${colorOption?.meta?.id}`);
    } else {
      setSelectedColor(colorOption);
      setColorDetailVisible(true);
    }
  };

  return (
    <SuiBox sx={{ minHeight: "100%" }} my={1}>
      {/* <SuiBox
        mb={1}
        ml={0.5}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        gap={1}
      >
        <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
          Number of Styles ({project?.products ? project.products.length : 0})
        </SuiTypography>
      </SuiBox> */}
      {project?.products && project.products.length > 0 ? (
        <SuiBox>
          <SuiBox mb={1}>
            <Tabs
              type="card"
              items={project.products.map((p, index) => {
                const designImages = p?.style?.imageFiles || [];
                let techPacksFiles = p?.style?.techPacksFiles || [];

                techPacksFiles = getStyleImages(p, false, ["techPacksFiles"]); // include online techpack files

                const allImageFiles = [...designImages, ...techPacksFiles];
                const styleImage = allImageFiles.length > 0 ? allImageFiles[0] : null;
                const zipFIleName = project.name + "-" + (p?.title || "style");

                const colorIds = p?.style?.color_way?.map((x) => x.value) || [];
                const color_way = colorOptions.filter((x) => colorIds.includes(x.value));

                let sizeChart = sizeOptions.filter((x) => x.value == p?.style?.sizeChart?.value);
                if (sizeChart && sizeChart.length > 0) {
                  sizeChart = sizeChart[0];
                }

                return {
                  label: (
                    <SuiBox
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      gap={0.5}
                    >
                      <ImageCardWrapper data={styleImage} height={20} width={20} iconMode />

                      <div>
                        <Tooltip title={p.title}>
                          <SuiTypography
                            variant="body1"
                            fontWeight="medium"
                            color="dark"
                            sx={{
                              textAlign: "center",
                              maxWidth: 120,
                              minWidth: 30,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: "1",
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {p.title}
                          </SuiTypography>
                        </Tooltip>
                      </div>
                    </SuiBox>
                  ),
                  key: "item_" + index,
                  children: (
                    <SuiBox>
                      <Grid container spacing={1}>
                        {allImageFiles.length > 0 ? (
                          <Grid item xs={12} sm={12} md={6}>
                            <SuiBox sx={{ height: "100%" }}>
                              <StyleImages
                                designFiles={designImages}
                                techPacksFiles={techPacksFiles}
                              />
                            </SuiBox>
                          </Grid>
                        ) : null}
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={allImageFiles.length > 0 ? 5 : 6}
                          sx={{ mx: allImageFiles.length > 0 ? "auto" : 1 }}
                        >
                          <SuiBox>
                            <SuiBox display="flex" alignItems="center">
                              {project.status == "active" || account.account_type == "de" ? (
                                <SuiTypography variant="h5" fontWeight="bold">
                                  <Link
                                    to={`/workspace/styles-master?id=${p?.id}&pid=${project.id}${
                                      account.account_type == "ma" ? "&stage=tech-pack" : ""
                                    }`}
                                  >
                                    {p.title}
                                  </Link>{" "}
                                </SuiTypography>
                              ) : (
                                <SuiTypography variant="h5" fontWeight="bold">
                                  {p.title}
                                </SuiTypography>
                              )}

                              <SuiTypography
                                variant="h6"
                                fontWeight="regular"
                                color="secondary"
                                component="span"
                                sx={{ ml: 1, whiteSpace: "nowrap" }}
                              >
                                ( {p?.style?.style_number})
                              </SuiTypography>
                              <Tooltip title="Download all information about the style">
                                <SuiBox
                                  sx={{ ml: 1 }}
                                  component={"a"}
                                  href={"#"}
                                  download
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDownloadStyle(
                                      [...designImages, ...techPacksFiles],
                                      zipFIleName
                                    );
                                  }}
                                >
                                  <FileDownloadOutlinedIcon fontSize="medium" />
                                </SuiBox>
                              </Tooltip>
                              <SuiBox mr={3}></SuiBox>
                              {account.account_type == "de" ? (
                                <Tooltip title="Edit the style">
                                  <SuiTypography
                                    variant="body1"
                                    color="dark"
                                    sx={{
                                      ml: 1,
                                      whiteSpace: "nowrap",
                                      textDecoration: "underline",
                                    }}
                                    component={Link}
                                    to={`/workspace/styles-master?id=${p?.id}&pid=${project.id}`}
                                  >
                                    Edit Style
                                  </SuiTypography>
                                </Tooltip>
                              ) : null}

                              {project.status == "active" || account.account_type == "de" ? (
                                <Tooltip title="View tech packs">
                                  <SuiTypography
                                    variant="body1"
                                    color="dark"
                                    sx={{
                                      ml: 1,
                                      whiteSpace: "nowrap",
                                      textDecoration: "underline",
                                    }}
                                    component={Link}
                                    to={`/workspace/styles-master?id=${p?.id}&pid=${project.id}&stage=tech-pack`}
                                  >
                                    Tech Pack
                                  </SuiTypography>
                                </Tooltip>
                              ) : null}

                              {project.status == "active" ? (
                                <Tooltip title="View sample">
                                  <SuiTypography
                                    variant="body1"
                                    color="dark"
                                    sx={{
                                      ml: 1,
                                      whiteSpace: "nowrap",
                                      textDecoration: "underline",
                                    }}
                                    component={Link}
                                    to={`/workspace/styles-master?id=${p?.id}&pid=${project.id}&stage=sample`}
                                  >
                                    Sample
                                  </SuiTypography>
                                </Tooltip>
                              ) : null}
                            </SuiBox>

                            <SuiBox mx={0.5}>
                              <SuiBox my={1}>
                                <SuiTypography variant="h6" fontWeight="regular" color="secondary">
                                  {p?.type && typeof p.type == "object" ? p.type.value : p?.type}
                                </SuiTypography>
                              </SuiBox>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={5} lg={4}>
                                  <SuiBox>
                                    <SuiBox mt={1}>
                                      <SuiTypography
                                        component="label"
                                        variant="button"
                                        fontWeight="bold"
                                      >
                                        Target Unit Cost
                                      </SuiTypography>
                                    </SuiBox>
                                    <SuiBox mb={1}>
                                      <SuiTypography
                                        variant="body1"
                                        textTransform="none"
                                        color="text"
                                        sx={{ ml: 0.5 }}
                                      >
                                        {p?.style?.cost_per_unit ? (
                                          <Tooltip title="Target unit cost.">
                                            <SuiBox sx={{ width: "min-content" }}>
                                              <NumberFormat
                                                value={p.style.cost_per_unit}
                                                displayType={"text"}
                                                thousandSeparator={true}
                                                prefix={"$"}
                                                renderText={(value, props) => (
                                                  <SuiTypography
                                                    variant="body1"
                                                    textTransform="none"
                                                    color="text"
                                                    sx={{ ml: 0.5 }}
                                                    {...props}
                                                  >
                                                    {value}
                                                  </SuiTypography>
                                                )}
                                              />
                                            </SuiBox>
                                          </Tooltip>
                                        ) : (
                                          "N/A"
                                        )}
                                      </SuiTypography>
                                    </SuiBox>
                                  </SuiBox>
                                </Grid>
                                <Grid item xs={12} md={5} lg={4}>
                                  <SuiBox>
                                    <SuiBox mt={1}>
                                      <SuiTypography
                                        component="label"
                                        variant="button"
                                        fontWeight="bold"
                                      >
                                        Quantity
                                      </SuiTypography>
                                    </SuiBox>
                                    <SuiBox mb={1}>
                                      <SuiTypography
                                        variant="body1"
                                        textTransform="none"
                                        color="text"
                                        sx={{ ml: 0.5 }}
                                      >
                                        {p?.style?.quantity ? (
                                          <SuiBox sx={{ width: "min-content" }}>
                                            <SuiTypography
                                              variant="body1"
                                              textTransform="none"
                                              color="text"
                                              sx={{ ml: 0.5 }}
                                            >
                                              {p?.style?.quantity}
                                            </SuiTypography>
                                          </SuiBox>
                                        ) : (
                                          "N/A"
                                        )}
                                      </SuiTypography>
                                    </SuiBox>
                                  </SuiBox>
                                </Grid>
                              </Grid>
                            </SuiBox>

                            <SuiBox mt={3}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={12} lg={12}>
                                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SuiTypography
                                      component="label"
                                      variant="button"
                                      fontWeight="bold"
                                    >
                                      Colorway
                                    </SuiTypography>
                                  </SuiBox>
                                  {color_way.length > 0 ? (
                                    <SuiBox ml={0.5}>
                                      {color_way.map((x) => (
                                        <Tag
                                          color={x?.hex || "#000000"}
                                          onClick={() => handleShowColorDetail(x)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          {x.label}
                                        </Tag>
                                      ))}
                                    </SuiBox>
                                  ) : (
                                    <SuiTypography
                                      variant="body1"
                                      textTransform="none"
                                      color="text"
                                      sx={{ ml: 0.5 }}
                                    >
                                      N/A
                                    </SuiTypography>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={5} lg={4}>
                                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SuiTypography
                                      component="label"
                                      variant="button"
                                      fontWeight="bold"
                                    >
                                      Size Chart
                                    </SuiTypography>
                                  </SuiBox>
                                  {sizeChart.value ? (
                                    <SuiTypography
                                      variant="body1"
                                      textTransform="none"
                                      color="text"
                                      sx={{ ml: 0.5 }}
                                    >
                                      <Link to={`/setup/size-master/${sizeChart.value}`}>
                                        {sizeChart.label}
                                      </Link>
                                    </SuiTypography>
                                  ) : (
                                    <SuiTypography
                                      variant="body1"
                                      textTransform="none"
                                      color="text"
                                      sx={{ ml: 0.5 }}
                                    >
                                      N/A
                                    </SuiTypography>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={5} lg={4}>
                                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                    <SuiTypography
                                      component="label"
                                      variant="button"
                                      fontWeight="bold"
                                    >
                                      Size Range
                                    </SuiTypography>
                                  </SuiBox>
                                  {p?.style?.sizeRange ? (
                                    <SuiTypography
                                      variant="body1"
                                      textTransform="none"
                                      color="text"
                                      sx={{ ml: 0.5 }}
                                    >
                                      {p.style.sizeRange}
                                    </SuiTypography>
                                  ) : (
                                    <SuiTypography
                                      variant="body1"
                                      textTransform="none"
                                      color="text"
                                      sx={{ ml: 0.5 }}
                                    >
                                      N/A
                                    </SuiTypography>
                                  )}
                                </Grid>
                              </Grid>
                            </SuiBox>
                            <SuiBox>
                              <SuiBox mb={1} ml={0.5} mt={3}>
                                <SuiTypography
                                  variant="button"
                                  fontWeight="bold"
                                  textTransform="capitalize"
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  Description:
                                </SuiTypography>
                              </SuiBox>
                              <SuiBox
                                mb={1}
                                ml={0.5}
                                display="flex"
                                justifyContent="flex-start"
                                alignItems="top"
                                gap={1}
                              >
                                <SuiTypography variant="body1" textTransform="none" color="text">
                                  {p?.description ? (
                                    <ReadMore
                                      limit={1200}
                                      variant="body1"
                                      textTransform="none"
                                      color="text"
                                    >
                                      {p.description}
                                    </ReadMore>
                                  ) : (
                                    "There is no description for this style."
                                  )}
                                </SuiTypography>
                              </SuiBox>
                            </SuiBox>
                          </SuiBox>
                        </Grid>
                      </Grid>
                    </SuiBox>
                  ),
                };
              })}
            />
          </SuiBox>
        </SuiBox>
      ) : null}
      {colorDetailVisible && selectedColor ? (
        <CreateColorModal
          handleClose={() => setColorDetailVisible(false)}
          visible={colorDetailVisible}
          isEdit={true}
          handleRefresh={null}
          color={selectedColor?.meta}
          readOnly
        />
      ) : null}
    </SuiBox>
  );
};

export default ProjectStyles;
